import React from "react";
import { removeSpecialCharacters } from "../../../utils/utils";
import moment from "moment";
import { usd } from "../../../utils/utils";

export const generateLoanDescription = (selectedLoan, showLastFour = false) => {
  const lastFour = showLastFour ? formatLastFour(selectedLoan.LastFour) : "";

  return `${selectedLoan.Collateral || selectedLoan.LoanType} ${lastFour}`;
};

export const formatLastFour = (lastFour) => {
  return `*${lastFour}`;
};

export const determineOneTimePaymentsByline = (
  hasSelectedPayoff,
  amountPastDue,
  payoffAmount,
  regularPayment,
  isCreditCard = false,
  amountDue = null,
) => {
  if (isCreditCard) {
    return `Your amount due is ${usd.format(amountDue)}`;
  } else if (hasSelectedPayoff) {
    return `Your payoff amount is ${usd.format(payoffAmount)}.`;
  } else if (amountPastDue) {
    return `The amount to bring your loan current is ${usd.format(amountPastDue)}`;
  } else {
    return `Your full monthly payment amount is ${usd.format(regularPayment)}`;
  }
};

export const principalOnlyMessage = (isPrincipalOnly) => {
  if (!isPrincipalOnly) {
    return null;
  }
  return (
    <div className="bold payment-message p-3 mt-3" style={{ fontStyle: "italic" }}>
      Payments made towards the principal only will not affect the next month's payment.
    </div>
  );
};

export const payoffAmountExceedsAchLimit = (isPayoff, payoffAmount, achLimit) => {
  if (isPayoff && achLimit && payoffAmount > achLimit) {
    return (
      <div className="payment-warning primary my-4 p-2 pl-3" style={{ borderRadius: "5px" }}>
        Your payoff amount exceeds the maximum transaction limit of {usd.format(achLimit)}. Please
        give us a call at 888.267.7200 to pay off your loan.
      </div>
    );
  }

  return null;
};

export const isExcessPayment = (inputAmount, amountDue, principal) => {
  const showMessage =
    inputAmount &&
    removeSpecialCharacters(inputAmount) > amountDue &&
    removeSpecialCharacters(inputAmount) < principal;

  if (showMessage) {
    return (
      <div className="p-3 mt-3 f14 payment-message">
        Payment in excess of the amount due will not affect next month's payment. Your payment will
        be applied to daily accrued interest and principal.
      </div>
    );
  }

  return null;
};

export const ninetyDaysOverdueMessage = (amountPastDue, is90DaysOverdue, isPayoff) => {
  if (!is90DaysOverdue || isPayoff) {
    return null;
  }

  return (
    <div className="payoff-msg primary my-4" style={{ borderRadius: "5px" }}>
      {usd.format(amountPastDue)} is the amount due to bring your loan current. To pay a different
      amount, contact us at 517.267.7234.
    </div>
  );
};

export const creditCardPaymentScheduledTodayMessage = (isCreditCard, hasPaymentScheduled) => {
  if (!hasPaymentScheduled || !isCreditCard) {
    return null;
  }

  return (
    <div className="payment-message primary p-2 my-3" style={{ borderRadius: "5px" }}>
      A payment is already in process for your Credit Card.
    </div>
  );
};

export const recurringPaymentScheduledTodayMessage = (
  pendingRecurringPaymentDate,
  recurringPayment,
) => {
  if (!pendingRecurringPaymentDate || !recurringPayment) {
    return null;
  }

  return (
    <div className="p-2 my-3 payment-message">
      <span className="bold">
        Your auto-payment scheduled for {moment(pendingRecurringPaymentDate).format("MM/DD/YYYY")}{" "}
        is already being processed.
      </span>{" "}
      Any changes made today will be reflected in your next scheduled payment.
    </div>
  );
};

export const determinePayoffInfoClosedEnd = (amount, principal, tenDayPayoff, changedToPayoff) => {
  const rawAmount = amount && parseFloat(removeSpecialCharacters(amount));

  const betweenPayoffAmounts =
    rawAmount && rawAmount >= principal && rawAmount < tenDayPayoff ? true : false;
  const greaterThanTenDay = rawAmount && rawAmount > tenDayPayoff ? true : false;
  const equalToTenDay = rawAmount && rawAmount === tenDayPayoff ? true : false;

  const greaterOrEqualToTenDay = equalToTenDay || greaterThanTenDay;

  let message = "";
  let className = "payment-message";
  let component = null;

  if (betweenPayoffAmounts) {
    message = `If you wish to pay off your loan, please pay ${usd.format(tenDayPayoff)}.`;
  }

  if (greaterThanTenDay) {
    className = "payment-warning";
    message = `Payment Amount cannot exceed ${usd.format(tenDayPayoff)}.`;
  }

  if (equalToTenDay || (betweenPayoffAmounts && changedToPayoff)) {
    message = "The payment amount entered will result in the payoff of this loan.";
  }

  if (betweenPayoffAmounts || greaterThanTenDay || equalToTenDay) {
    component = <div className={"p-3 mt-3 f14 " + className}>{message}</div>;
  }

  return {
    component: component,
    disabled: greaterThanTenDay,
    greaterOrEqualToPayoff: greaterOrEqualToTenDay,
  };
};

export const determinePayoffInfoOpenEnd = (amount, minimumPayoff, principalBalance) => {
  const rawAmount = amount && parseFloat(removeSpecialCharacters(amount));

  const betweenBalanceAndMinimumPayoff =
    rawAmount && rawAmount >= principalBalance && rawAmount < minimumPayoff ? true : false;

  const greaterThanMinimumPayoff = rawAmount && rawAmount > minimumPayoff ? true : false;

  const equalToMinimumPayoff = rawAmount && rawAmount === minimumPayoff ? true : false;

  const greaterOrEqualToMinimumPayoff = equalToMinimumPayoff || greaterThanMinimumPayoff;

  let message = "";
  let className = "payment-message";
  let component = null;

  if (greaterThanMinimumPayoff || betweenBalanceAndMinimumPayoff) {
    message = `To pay your full balance, please pay ${usd.format(minimumPayoff)}.`;
  }

  if (equalToMinimumPayoff) {
    message = "The payment amount entered will pay the total balance on your loan.";
  }

  if (greaterThanMinimumPayoff || betweenBalanceAndMinimumPayoff || equalToMinimumPayoff) {
    component = <div className={"p-3 mt-3 f14 " + className}>{message}</div>;
  }

  return {
    component: component,
    disabled: greaterThanMinimumPayoff,
    greaterOrEqualToPayoff: greaterOrEqualToMinimumPayoff,
  };
};

export const determinePayoffInfoEscrow = (amount, payoff) => {
  const rawAmount = amount && parseFloat(removeSpecialCharacters(amount));
  const greaterThanMaxAllowed = rawAmount && rawAmount > payoff ? true : false;
  const greaterOrEqualToPayoff = rawAmount && rawAmount >= payoff ? true : false;

  let message = "";
  let className = "payment-warning";
  let component = null;

  if (greaterThanMaxAllowed) {
    message = `Payment Amount cannot exceed ${usd.format(payoff)}.`;
    component = <div className={"p-3 mt-3 f14 " + className}>{message}</div>;
  }

  return {
    component: component,
    disabled: greaterThanMaxAllowed,
    greaterOrEqualToPayoff: greaterOrEqualToPayoff,
  };
};



// WEBPACK FOOTER //
// ./src/components/authenticated-payments/misc/messaging-utils.js